import React, { useEffect, useState, useRef } from 'react';
import { Search, Database, UserPlus } from 'lucide-react';
import api from '../../services/api';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
import alertService from '../../utils/alert';

const LEAD_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'company', 'title', 'website', 'linkedinUrl', 'source', 'notes', 'status'];

const STATUS_BADGE = {
  pending: 'bg-[#FFF4E5] text-[#8C4B02]',
  processing: 'bg-[#D6EAFB] text-[#0176D3]',
  completed: 'bg-[#E3F3E8] text-[#036B3A]',
  failed: 'bg-[#FDE7E9] text-[#BA0517]',
};

// ── CSV Import Wizard ─────────────────────────────────────────────────────────

function CSVImportWizard({ onImported }) {
  const [step, setStep] = useState(1);
  const [jobId, setJobId] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);
  const fileRef = useRef();

  const upload = async () => {
    if (!fileRef.current.files[0]) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', fileRef.current.files[0]);
    try {
      const { data } = await api.post('/enrich/csv/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setJobId(data.data.jobId);
      setHeaders(data.data.headers || []);
      const autoMap = {};
      for (const h of data.data.headers) {
        const match = LEAD_FIELDS.find(f => f.toLowerCase() === h.toLowerCase().replace(/[_ ]/g, ''));
        if (match) autoMap[h] = match;
      }
      setMapping(autoMap);
      setStep(2);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const startImport = async () => {
    setStarting(true);
    try {
      await api.post(`/enrich/csv/${jobId}/start`, { fieldMapping: mapping });
      setStep(3);
      onImported();
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <h3 className="font-bold text-[#2E2E2E] mb-4">CSV Import Wizard</h3>

      {step === 1 && (
        <div>
          <p className="text-sm text-[#706E6B] mb-3">Upload a CSV file to import leads</p>
          <div className="flex gap-3 items-center">
            <input ref={fileRef} type="file" accept=".csv" className="text-sm text-[#5C5C5C]" />
            <button onClick={upload} disabled={uploading}
              className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-4 py-2 rounded text-[13px] font-semibold whitespace-nowrap">
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <p className="text-sm text-[#706E6B] mb-4">Map CSV columns to lead fields</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {headers.map(h => (
              <div key={h} className="flex items-center gap-3">
                <span className="text-sm font-mono text-[#444444] w-36 shrink-0 truncate">{h}</span>
                <span className="text-[#706E6B]">→</span>
                <select
                  value={mapping[h] || ''}
                  onChange={e => setMapping(m => ({ ...m, [h]: e.target.value }))}
                  className="flex-1 border border-[#D8DDE6] rounded px-3 py-1.5 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                >
                  <option value="">(skip)</option>
                  {LEAD_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setStep(1)} className="px-4 py-2 border border-[#E5E5E5] rounded text-sm whitespace-nowrap">Back</button>
            <button onClick={startImport} disabled={starting}
              className="bg-[#2E844A] hover:bg-[#256B3C] disabled:opacity-50 text-white px-6 py-2 rounded text-[13px] font-semibold whitespace-nowrap">
              {starting ? 'Starting…' : 'Start Import'}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="text-center py-4">
          <p className="text-2xl mb-2">🎉</p>
          <p className="text-sm text-[#444444] font-medium">Import started! Check the job list for progress.</p>
          <button onClick={() => setStep(1)} className="mt-3 text-sm text-[#0176D3] hover:underline">Import another file</button>
        </div>
      )}
    </div>
  );
}

// ── Apollo Search ─────────────────────────────────────────────────────────────

function ApolloSearch() {
  const [query, setQuery] = useState({ name: '', company: '', title: '', location: '' });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(query).filter(([, v]) => v))).toString();
      const { data } = await api.get(`/enrich/apollo/search?${params}`);
      setResults(data.data || []);
    } finally {
      setLoading(false);
    }
  };

  const importSelected = async () => {
    setImporting(true);
    try {
      const leads = results.filter((_, i) => selected.has(i));
      const { data } = await api.post('/enrich/apollo/import', { leads });
      alertService.success('Imported', `Imported ${data.data.imported} leads`);
      setSelected(new Set());
    } finally {
      setImporting(false);
    }
  };

  const toggleAll = () => {
    if (selected.size === results.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.map((_, i) => i)));
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <h3 className="font-bold text-[#2E2E2E] mb-4">Lead Scraping (Apollo.io)</h3>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {[['name', 'Name'], ['company', 'Company'], ['title', 'Job Title'], ['location', 'Location']].map(([k, l]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-[#706E6B] mb-1">{l}</label>
            <input value={query[k]} onChange={e => setQuery(q => ({ ...q, [k]: e.target.value }))}
              className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              placeholder={l} />
          </div>
        ))}
      </div>
      <button onClick={search} disabled={loading}
        className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-5 py-2 rounded text-[13px] font-semibold mb-4 whitespace-nowrap">
        {loading ? 'Searching…' : 'Search'}
      </button>

      {results.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-[#706E6B]">{results.length} results</p>
            <div className="flex gap-2">
              <button onClick={toggleAll} className="text-xs text-[#0176D3] hover:underline">
                {selected.size === results.length ? 'Deselect all' : 'Select all'}
              </button>
              {selected.size > 0 && (
                <button onClick={importSelected} disabled={importing}
                  className="bg-[#2E844A] hover:bg-[#256B3C] disabled:opacity-50 text-white px-3 py-1 rounded text-xs font-medium whitespace-nowrap">
                  {importing ? 'Importing…' : `Import (${selected.size})`}
                </button>
              )}
            </div>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {results.map((r, i) => (
              <div key={i}
                onClick={() => setSelected(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${selected.has(i) ? 'border-[#B8DAF3] bg-[#EAF4FE]' : 'border-transparent hover:bg-[#F7F9FB]'}`}
              >
                <input type="checkbox" checked={selected.has(i)} onChange={() => {}} className="h-4 w-4 text-[#0176D3]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2E2E2E]">{r.firstName} {r.lastName}</p>
                  <p className="text-xs text-[#706E6B] truncate">{r.title}{r.company ? ` at ${r.company}` : ''}</p>
                  {r.email && <p className="text-xs text-[#0176D3]">{r.email}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function EnrichPage() {
  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('lead_enrichment');
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [tab, setTab] = useState('csv');

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      const { data } = await api.get('/enrich/jobs');
      setJobs(data.data || []);
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => { if (hasAccess) loadJobs(); }, [hasAccess]);

  if (featureLoading) return null;

  if (!hasAccess) {
    return (
      <FeatureLockedPage
        title="Lead Enrichment"
        subtitle="Fill in the gaps on every lead automatically"
        icon={Search}
        plan={plan}
        featureName="Lead Enrichment"
        description="Upgrade to unlock CSV bulk import, on-demand contact enrichment, and Apollo people-search directly into your CRM."
        highlights={[
          { icon: Database, label: 'Bulk CSV import', desc: 'Async import with per-row progress tracking' },
          { icon: UserPlus, label: 'Auto-fill profiles', desc: 'Title, company, and industry from just an email' },
          { icon: Search, label: 'Prospect search', desc: 'Find new leads matching your ideal customer' },
        ]}
      />
    );
  }

  return (
    <div className={PAGE}>
      <div className="mb-6"><PageHeader title="Lead Enrichment" /></div>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {[['csv', '📄 CSV Import'], ['apollo', '🔍 Apollo Search']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded text-[13px] font-semibold whitespace-nowrap shrink-0 ${tab === k ? 'bg-[#0176D3] text-white' : 'bg-white border border-[#E5E5E5] text-[#5C5C5C] hover:bg-[#F7F9FB]'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'csv' && <CSVImportWizard onImported={loadJobs} />}
      {tab === 'apollo' && <ApolloSearch />}

      {/* Import jobs */}
      <div className="mt-8">
        <h2 className="text-base font-bold text-[#2E2E2E] mb-3">Import Jobs</h2>
        {loadingJobs && <p className="text-sm text-[#706E6B]">Loading…</p>}
        <div className="space-y-2">
          {jobs.map(job => (
            <div key={job._id} className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex items-center gap-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[job.status]}`}>{job.status}</span>
                  <p className="text-sm font-medium text-[#444444] capitalize">{job.type.replace('_', ' ')}</p>
                </div>
                <p className="text-xs text-[#706E6B] mt-0.5">{new Date(job.createdAt).toLocaleString()}</p>
              </div>
              <div className="text-right">
                {job.totalRecords > 0 && (
                  <>
                    <p className="text-sm font-medium text-[#444444]">{job.processedRecords}/{job.totalRecords}</p>
                    <div className="w-24 bg-[#E5E5E5] rounded-full h-1.5 mt-1">
                      <div className="bg-[#0176D3] h-1.5 rounded-full" style={{ width: `${(job.processedRecords / job.totalRecords) * 100}%` }} />
                    </div>
                    <p className="text-xs text-[#706E6B] mt-0.5">{job.successCount} ok · {job.failCount} failed</p>
                  </>
                )}
              </div>
            </div>
          ))}
          {!loadingJobs && jobs.length === 0 && (
            <p className="text-sm text-[#706E6B]">No import jobs yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
